// src/components/pages/ConfirmWrapperArea.jsx
import React, { useState, useContext, useEffect } from "react";
import { Link } from "react-router-dom";
import useSWR from "swr";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { SessionContext } from "../../common/SessionContext";

const scrollTop = () =>
  window.scrollTo({ top: 0, behavior: "smooth" });

export default function ConfirmWrapperArea(props) {
  const { session } = useContext(SessionContext);
  const currentCurrency = session.currency;

  const API_URL = `${process.env.REACT_APP_API_FOLDER}get_unpaid_booking/${props.ctype}/${props.hash}`;
  const API_URL2 = `${process.env.REACT_APP_API_FOLDER}grantcode/`;
  const API_TOKEN = process.env.REACT_APP_API_TOKEN;

  // SWR fetcher
  const fetcher = url =>
    fetch(url, { headers: { "X-API-TOKEN": API_TOKEN } })
      .then(res => {
        if (!res.ok) throw new Error(`Network response was not ok: ${res.status}`);
        return res.json();
      });

  const { data: booking = {}, error, isLoading } = useSWR(API_URL, fetcher);

  // Local UI state
  const [currentStep, setCurrentStep] = useState(0);
  const [extras, setExtras] = useState(false);
  const [addWw, setAddWw] = useState(0);
  const [paymentType, setPaymentType] = useState("");
  const [grantCode, setGrantCode] = useState("");
  const [grantMsg, setGrantMsg] = useState(null);
  const [discountValue, setDiscountValue] = useState("");
  const [codeType, setCodeType] = useState("");

  // Pricing derived
  const pricing = booking.pricing?.[currentCurrency] || {};
  const {
    full_amount: fullAmount,
    full_amount_inc_ww: fullAmountWW,
    number_of_payments: numberOfPayments,
    work_week: workWeek,
    symbol: currencySymbol
  } = pricing;

  const [finalAmount, setFinalAmount] = useState(fullAmount);
  const [finalSplitAmount, setFinalSplitAmount] = useState(
    numberOfPayments ? fullAmount / numberOfPayments : ""
  );

  // On mount, set codeType
  useEffect(() => {
    setCodeType(props.ctype === "online" ? "bundle" : "grant");
  }, [props.ctype]);

  // Whenever booking or extras change, recalc final amounts
  useEffect(() => {
    const base = extras ? fullAmountWW : fullAmount;
    const cleaned = Number(base);
    const discounted = discountValue
      ? Math.round(cleaned * ((100 - Number(discountValue)) / 100))
      : cleaned;
    setFinalAmount(discounted);
    setFinalSplitAmount(numberOfPayments ? discounted / numberOfPayments : "");
  }, [fullAmount, fullAmountWW, discountValue, extras, numberOfPayments]);

  const nextStep = () => setCurrentStep(s => s + 1);
  const previousStep = () => setCurrentStep(s => s - 1);

  const handleExtrasYes = () => {
    setExtras(true);
    setAddWw(1);
    nextStep();
  };
  const handleExtrasNo = () => {
    setExtras(false);
    setAddWw(0);
    nextStep();
  };

  const handleAddGrantCode = async e => {
    e.preventDefault();
    try {
      const res = await fetcher(
        API_URL2,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-API-TOKEN": API_TOKEN
          },
          body: JSON.stringify({
            grantCode,
            unpaidHash: props.hash,
            codeType
          })
        }
      );
      if (res.apply_dcode) {
        setGrantMsg(<span className="grantcode-pass">Code applied!</span>);
        setDiscountValue(res.dcode_value);
      } else {
        setGrantMsg(
          <span className="grantcode-fail">
            That code didn’t work – {res.failcode}
          </span>
        );
      }
    } catch {
      setGrantMsg(<span className="grantcode-fail">Error applying code</span>);
    }
  };

  if (isLoading) {
    return (
      <div className="contact-wrapper pt-110">
        <div className="container">
          <div className="row align-items-center justify-content-lg-center gy-5">
            <div className="col-lg-6">
              <Skeleton height={300} />
            </div>
            <div className="col-lg-5">
              <Skeleton height={40} width="70%" style={{ marginBottom: 16 }} />
              <Skeleton count={4} />
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <p className="text-danger">Error loading booking: {error.message}</p>
    );
  }

  // Build priceCode string
  let priceCode = "";
  if (booking.pricecode_version == 2) priceCode += "v2_";
  priceCode += booking.course_id;
  if (props.ctype == "online") priceCode += "_ol";
  priceCode += `_${currentCurrency}`;
  if (discountValue) priceCode += `_${discountValue}`;
  priceCode += `_${numberOfPayments}`;
  if (extras) priceCode += "_ww";

  const sliderStyle = {
    display: "flex",
    transform: `translateX(-${currentStep * 100}%)`,
    transition: "transform 0.5s ease",
    width: `${(props.ctype === "onsite" ? 3 : 2) * 100}%`
  };

  return (
    <div className="contact-wrapper pt-110">
      <div className="container">
        <div className="row align-items-center justify-content-lg-center gy-5">
          <div className="col-lg-6">
            <div className="contatc-intro-figure">
              <img
                src={`${process.env.PUBLIC_URL}/images/banner/booked-bg.png`}
                alt="ContactBannerIMG"
                className="img-fluid"
              />
            </div>
          </div>
          <div className="col-lg-5">
            <h3>Hey, {booking.firstname}!</h3>
            <div className="slider-wrapper" style={{ overflow: "hidden" }}>
              <ul className="slider-container" style={sliderStyle}>
                {/* STEP 1 */}
                <li className="confirmStep" style={{ width: "100%" }}>
                  <h6 className="mt-10">
                    You’re one step away from confirming your place at CAMP for
                    this epic workshop.
                  </h6>
                  <button
                    className="button-fill-primary confirm-process-button"
                    onClick={nextStep}
                  >
                    Ok, let’s go!
                  </button>
                </li>

                {/* STEP 1.5 (onsite only) */}
                {props.ctype === "onsite" && (
                  <li className="confirmStep" style={{ width: "100%" }}>
                    <p>
                      Now is the best time to decide if you want to{" "}
                      <strong>add 7 nights</strong> to your stay. Hang out at CAMP
                      (with full catering), do some hiking, use our facilities,
                      work on your projects and process the past week. Full +7 info
                      is{" "}
                      <Link to="/plus7" onClick={scrollTop}>
                        here
                      </Link>
                      .
                    </p>
                    <p>
                      It costs <strong>{currencySymbol}{workWeek}</strong>, which
                      covers full board accommodation and unlimited use of CAMP’s
                      facilities. By adding +7 now, you can split the cost along
                      with the workshop fee. You can add this later, but you’ll need
                      to pay the extra cost in full at that time.
                    </p>
                    <button
                      className="button-fill-primary confirm-process-button"
                      onClick={handleExtrasYes}
                    >
                      Yes, add +7
                    </button>
                    <button
                      className="button-fill-primary confirm-process-button"
                      onClick={handleExtrasNo}
                    >
                      No thanks
                    </button>
                  </li>
                )}

                {/* STEP 2 */}
                <li className="confirmStep" style={{ width: "100%" }}>
                  {extras ? (
                    <p>
                      The total cost of your workshop, including the extra 7
                      days, is{" "}
                      <strong>{currencySymbol}{finalAmount}</strong>.
                    </p>
                  ) : (
                    <p>
                      The total cost of your workshop is{" "}
                      <strong>{currencySymbol}{finalAmount}</strong>.
                    </p>
                  )}
                  <p id="pay_instruct">
                    Please select one of the options below – you can pay in
                    full, or start a {numberOfPayments}-month payment
                    plan.
                  </p>

                  {/* Grant code input */}
                  {props.ctype !== "online" ? (
                    props.ctype === "onsite" && (
                      <>
                        {grantMsg}
                        <div style={{ display: "flex", gap: 10, marginBottom: 15 }}>
                          <input
                            type="text"
                            placeholder="Grant code"
                            value={grantCode}
                            onChange={e => setGrantCode(e.target.value)}
                            className="grantcode-input"
                            style={{ flex: 1 }}
                          />
                          <button
                            className="grantcode-button"
                            onClick={handleAddGrantCode}
                          >
                            Add code
                          </button>
                        </div>
                      </>
                    )
                  ) : (
                    <>
                      {grantMsg}
                      <div style={{ display: "flex", gap: 10, marginBottom: 15 }}>
                        <input
                          type="text"
                          placeholder="Bundle code"
                          value={grantCode}
                          onChange={e => setGrantCode(e.target.value)}
                          className="grantcode-input"
                          style={{ flex: 1 }}
                        />
                        <button
                          className="grantcode-button"
                          onClick={handleAddGrantCode}
                        >
                          Add bundle code
                        </button>
                      </div>
                    </>
                  )}

                  {/* Payment forms */}
                  <div style={{ display: "flex", gap: 10 }}>
                    <form
                      id="pay_full"
                      action="https://www.campfr.com/api/checkout"
                      method="POST"
                    >
                      <input type="hidden" name="product" value={booking.product} />
                      <input type="hidden" name="paymentType" value="payment" />
                      <input type="hidden" name="hash" value={props.hash} />
                      <input type="hidden" name="amount" value={finalAmount} />
                      <input type="hidden" name="currency" value={currentCurrency} />
                      <input type="hidden" name="priceCode" value={priceCode} />
                      <input type="hidden" name="ctype" value={props.ctype} />
                      <input type="hidden" name="add_ww" value={addWw} />
                      <input type="hidden" name="grantCode" value={grantCode} />
                      <input type="hidden" name="codeType" value={codeType} />
                      <input type="hidden" name="ws" value={booking.tracked_id} />
                      <button
                        className="confirm-process-button"
                        type="submit"
                        id="pay_full_button"
                      >
                        Pay in full
                      </button>
                    </form>
                    <form
                      id="pay_split"
                      action="https://www.campfr.com/api/checkout"
                      method="POST"
                    >
                      <input type="hidden" name="product" value={booking.product} />
                      <input
                        type="hidden"
                        name="paymentType"
                        value="subscription"
                      />
                      <input type="hidden" name="hash" value={props.hash} />
                      <input type="hidden" name="amount" value={finalAmount} />
                      <input type="hidden" name="currency" value={currentCurrency} />
                      <input type="hidden" name="priceCode" value={priceCode} />
                      <input type="hidden" name="ctype" value={props.ctype} />
                      <input type="hidden" name="add_ww" value={addWw} />
                      <input type="hidden" name="ws" value={booking.tracked_id} />
                      <button
                        className="confirm-process-button"
                        type="submit"
                      >
                        Split over {numberOfPayments} months
                      </button>
                    </form>
                  </div>
                </li>
              </ul>
            </div>
            <p>
              <i>
                If you have any questions about the workshop, the booking
                process or anything else, check the{" "}
                <Link to="/faq" onClick={scrollTop}>
                  FAQ
                </Link>
                , or <a href="mailto:hello@campfr.com">drop us a line</a>.
              </i>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
